import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, BACKEND_URL } from "../lib/api";
import { AnalyticsPanel } from "../components/AnalyticsPanel";
import { AdminProducts } from "../components/admin/AdminProducts";
import { AdminStockPanel } from "../components/admin/AdminStockPanel";
import { AdminCustomers } from "../components/admin/AdminCustomers";
import { AdminPromos } from "../components/admin/AdminPromos";
import { AdminContent } from "../components/admin/AdminContent";
import { AdminEmailLogs } from "../components/admin/AdminEmailLogs";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Check, X, Trash2, Truck, Download, Printer, MapPin, ChevronDown, ChevronUp, Home, Package } from "lucide-react";
import { useState as useLocalState } from "react";
import { toast } from "sonner";

const UNIVERSE_BADGE = {
  femme: { label: "FEMME", bg: "#E8B4B8", fg: "#2a1416" },
  homme: { label: "HOMME", bg: "#111111", fg: "#E7C979", bd: "#E7C979" },
  mixte: { label: "MIXTE", bg: "#8a6d3b", fg: "#fff" },
  all: { label: "—", bg: "transparent", fg: "var(--lo-muted)" },
};

function UniverseBadge({ u }) {
  const s = UNIVERSE_BADGE[u] || UNIVERSE_BADGE.all;
  return (
    <span className="text-[10px] font-semibold px-2.5 py-1 uppercase tracking-wider shrink-0"
      style={{ background: s.bg, color: s.fg, border: s.bd ? `1px solid ${s.bd}` : "none" }}
      data-testid={`order-universe-${u}`}>{s.label}</span>
  );
}

const UNIVERSE_LABELS = { femme: "FEMME", homme: "HOMME", mixte: "MIXTE", all: "—" };

function printOrder(o) {
  const uni = UNIVERSE_LABELS[o.universe] || "—";
  const date = o.created_at ? new Date(o.created_at).toLocaleString("fr-FR") : "—";
  const html = `<!doctype html><html lang="fr"><head><meta charset="utf-8"><title>Bon de préparation #${o.id.slice(-6)}</title>
  <style>
    *{box-sizing:border-box} body{font-family:Georgia,'Times New Roman',serif;color:#111;margin:0;padding:40px;}
    .hd{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #111;padding-bottom:16px;margin-bottom:24px}
    .brand{font-size:22px;letter-spacing:4px;font-weight:bold}
    .brand small{display:block;font-size:10px;letter-spacing:3px;color:#666;margin-top:4px}
    .uni{border:2px solid #111;padding:6px 16px;font-weight:bold;letter-spacing:2px;font-size:14px}
    h1{font-size:16px;letter-spacing:2px;text-transform:uppercase;margin:0 0 4px}
    .muted{color:#666;font-size:12px}
    table{width:100%;border-collapse:collapse;margin:24px 0}
    th,td{text-align:left;padding:10px 8px;border-bottom:1px solid #ddd;font-size:14px}
    th{font-size:11px;letter-spacing:1px;text-transform:uppercase;color:#666}
    .box{border:1px solid #ccc;padding:16px;margin-top:12px}
    .row{display:flex;gap:40px;margin-top:24px}
    .row>div{flex:1}
    .foot{margin-top:48px;font-size:11px;color:#999;text-align:center;border-top:1px solid #eee;padding-top:12px}
    @media print{body{padding:16px}}
  </style></head><body>
    <div class="hd">
      <div class="brand">LUXE ORYVEA <small>PREMIUM · SKIN &amp; HAIR EXCELLENCE</small></div>
      <div class="uni">UNIVERS : ${uni}</div>
    </div>
    <h1>Bon de préparation</h1>
    <p class="muted">Commande n° ${o.id.slice(-8)} · ${date} · Paiement : ${o.payment_status === "paid" ? "Payée" : "En attente"}</p>
    <table>
      <thead><tr><th>Produit</th><th>Quantité</th><th>Univers</th><th>Montant</th></tr></thead>
      <tbody><tr><td>${o.items_label || "-"}</td><td>${o.units || 0}</td><td>${uni}</td><td>${(o.amount || 0).toFixed(2).replace(".", ",")} €</td></tr></tbody>
    </table>
    <div class="row">
      <div><h1>Client</h1><div class="box">${o.full_name || "-"}<br>${o.email || ""}</div></div>
      <div><h1>Adresse de livraison</h1><div class="box">${(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? `<strong>Point Relais / Locker</strong><br>${o.shipping_address?.name || o.relay_name || "-"}<br>${o.shipping_address?.address || o.relay_address || "-"}<br>${o.shipping_address?.postal_code || o.relay_postal_code || ""} ${o.shipping_address?.city || o.relay_city || ""}<br>${o.shipping_address?.country || o.country || "France"}` : `${o.shipping_address?.address || o.address || "-"}<br>${o.shipping_address?.postal_code || o.postal_code || ""} ${o.shipping_address?.city || o.city || ""}<br>${o.shipping_address?.country || o.country || "France"}`}</div></div>
    </div>
    ${(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? `<div class="row"><div><h1>Adresse de facturation</h1><div class="box">${o.billing_address?.address || o.address || "-"}<br>${o.billing_address?.postal_code || o.postal_code || ""} ${o.billing_address?.city || o.city || ""}<br>${o.billing_address?.country || o.country || "France"}</div></div><div><h1>Mode de livraison</h1><div class="box">Point Relais / Locker · 4,90 €</div></div></div>` : ""}
    <div class="foot">LUXE ORYVEA PREMIUM · Bon de préparation généré le ${new Date().toLocaleString("fr-FR")}</div>
    <script>window.onload=function(){window.print();}</script>
  </body></html>`;
  const w = window.open("", "_blank", "width=800,height=900");
  if (w) { w.document.write(html); w.document.close(); }
}

function OrderRow({ o, onShipped }) {
  const [carrier, setCarrier] = useLocalState("Colissimo");
  const [tracking, setTracking] = useLocalState("");
  const [trackingNum, setTrackingNum] = useLocalState("");
  const [busy, setBusy] = useLocalState(false);
  const [showDelivery, setShowDelivery] = useLocalState(false);

  const ship = async () => {
    let url = tracking.trim();
    if (!url) { toast.error("Ajoutez un lien de suivi"); return; }
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/ship`, { carrier, tracking_url: url, tracking_number: trackingNum.trim() || null });
      toast.success("Commande expédiée · email envoyé");
      onShipped();
    } catch (e) {
      toast.error("Erreur lors de l'expédition");
    } finally { setBusy(false); }
  };

  const setStatus = async (status, label) => {
    setBusy(true);
    try {
      await api.post(`/admin/orders/${o.id}/status`, { status });
      toast.success(`${label} · email envoyé`);
      onShipped();
    } catch (e) {
      toast.error("Erreur lors de la mise à jour du statut");
    } finally { setBusy(false); }
  };

  return (
    <div className="card-lux p-5" data-testid="admin-order">
      <div className="flex justify-between items-start gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <UniverseBadge u={o.universe} />
            <span className="overline" style={{ fontSize: "0.55rem", color: "var(--lo-muted)" }}>#{o.id.slice(-6)}</span>
          </div>
          <p className="font-display text-lg">{o.full_name} — {o.items_label}</p>
          <p className="text-xs" style={{ color: "var(--lo-muted)" }}>{o.email} · {(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? `${o.shipping_address?.name || o.relay_name || "Point Relais / Locker"} · ${o.shipping_address?.address || o.relay_address || ""}, ${o.shipping_address?.postal_code || o.relay_postal_code || ""} ${o.shipping_address?.city || o.relay_city || ""}` : `${o.shipping_address?.address || o.address || ""}, ${o.shipping_address?.postal_code || o.postal_code || ""} ${o.shipping_address?.city || o.city || ""}, ${o.shipping_address?.country || o.country || "France"}`}</p>
          <p className="text-xs mt-1" style={{ color: "var(--lo-muted)" }}>Qté {o.units} · {o.created_at ? new Date(o.created_at).toLocaleString("fr-FR") : "—"}</p></div>
        <div className="text-right shrink-0"><p className="font-display text-xl" style={{ color: "var(--lo-rose-hi)" }}>{(o.amount || 0).toFixed(2).replace(".", ",")} €</p>
          <span className="overline" style={{ color: o.payment_status === "paid" ? "var(--lo-rose-hi)" : "var(--lo-muted)" }}>{o.payment_status === "paid" ? "Payée" : "En attente"}</span>
          {o.status && ["prepared", "shipped", "delivered", "cancelled", "refunded"].includes(o.status) && (
            <span className="block text-[10px] font-semibold uppercase tracking-wider mt-1" data-testid="order-status-badge" style={{ color: o.status === "cancelled" || o.status === "refunded" ? "#e07a7a" : "#7ee0a0" }}>
              {{ prepared: "En préparation", shipped: "Expédiée", delivered: "Livrée", cancelled: "Annulée", refunded: "Remboursée" }[o.status]}
            </span>
          )}
          <div className="flex flex-wrap justify-end gap-2 mt-2">
            <button onClick={() => setShowDelivery((v) => !v)} className="btn-ghost inline-flex items-center gap-1.5 h-8 px-3 text-xs" data-testid="order-delivery-details">
              {(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? <Package size={13} /> : <Home size={13} />}
              {showDelivery ? "Masquer la livraison" : "Voir la livraison"}
              {showDelivery ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            </button>
            <button onClick={() => printOrder(o)} className="btn-ghost inline-flex items-center gap-1.5 h-8 px-3 text-xs" data-testid="order-print"><Printer size={13} /> Bon de préparation</button>
          </div>
        </div>
      </div>
      {showDelivery && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--lo-border)" }} data-testid="order-delivery-details-panel">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-xl p-4" style={{ border: "1px solid var(--lo-border)", background: "rgba(255,255,255,0.02)" }}>
              <div className="flex items-center gap-2 mb-3">
                <MapPin size={15} style={{ color: "var(--lo-rose-hi)" }} />
                <span className="overline" style={{ color: "var(--lo-rose-hi)" }}>Adresse de livraison</span>
              </div>
              <p className="font-semibold mb-2">{(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? "Point Relais / Locker" : "Livraison à domicile"}</p>
              {(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? (
                <p className="text-sm leading-6">
                  <strong>{o.shipping_address?.name || o.relay_name || "Point Relais / Locker"}</strong><br />
                  {o.shipping_address?.address || o.relay_address || "Adresse non enregistrée"}<br />
                  {(o.shipping_address?.postal_code || o.relay_postal_code || "")} {(o.shipping_address?.city || o.relay_city || "")}<br />
                  {o.shipping_address?.country || o.country || "France"}
                </p>
              ) : (
                <p className="text-sm leading-6">
                  {o.shipping_address?.address || o.address || "Adresse non enregistrée"}<br />
                  {(o.shipping_address?.postal_code || o.postal_code || "")} {(o.shipping_address?.city || o.city || "")}<br />
                  {o.shipping_address?.country || o.country || "France"}
                </p>
              )}
              <div className="mt-3 text-xs" style={{ color: "var(--lo-muted)" }}>
                Mode : <strong>{(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) ? "Point Relais / Locker" : "Livraison à domicile"}</strong>
              </div>
              {(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) && (
                <div className="mt-2 text-xs" style={{ color: "var(--lo-muted)" }}>
                  Identifiant relais : {o.relay_id || "non renseigné"}
                </div>
              )}
            </div>
            <div className="rounded-xl p-4" style={{ border: "1px solid var(--lo-border)", background: "rgba(255,255,255,0.02)" }}>
              <div className="flex items-center gap-2 mb-3">
                <Home size={15} style={{ color: "var(--lo-rose-hi)" }} />
                <span className="overline" style={{ color: "var(--lo-rose-hi)" }}>Adresse de facturation</span>
              </div>
              <p className="text-sm leading-6">
                {o.billing_address?.address || o.address || "Adresse non enregistrée"}<br />
                {(o.billing_address?.postal_code || o.postal_code || "")} {(o.billing_address?.city || o.city || "")}<br />
                {o.billing_address?.country || o.country || "France"}
              </p>
            </div>
          </div>
          {(o.is_relay || ["relay", "point_relais", "point-relais", "point relais", "locker"].includes(String(o.shipping_method || "").toLowerCase())) && !o.relay_address && (
            <div className="mt-3 rounded-lg px-4 py-3 text-xs" style={{ border: "1px solid #9b5c5c", color: "#e7b4b4" }}>
              ⚠️ Cette commande est indiquée comme Point Relais, mais les informations du relais ne sont pas enregistrées dans la commande. Le client devra être vérifié manuellement.
            </div>
          )}
        </div>
      )}
      {o.payment_status === "paid" && (
        <div className="mt-4 pt-4 border-t" style={{ borderColor: "var(--lo-border)" }}>
          {o.shipped ? (
            <p className="overline flex items-center gap-2" style={{ color: "var(--lo-rose-hi)" }}><Truck size={14} /> Expédiée · <a href={o.tracking_url} target="_blank" rel="noreferrer" className="link-underline">Lien de suivi</a></p>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={carrier} onChange={(e) => setCarrier(e.target.value)} placeholder="Transporteur" className="bg-transparent border-b py-2 text-sm outline-none w-full sm:w-40" style={{ borderColor: "var(--lo-border)" }} data-testid="ship-carrier" />
              <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="Lien de suivi (URL)" className="bg-transparent border-b py-2 text-sm outline-none flex-1" style={{ borderColor: "var(--lo-border)" }} data-testid="ship-tracking" />
              <input value={trackingNum} onChange={(e) => setTrackingNum(e.target.value)} placeholder="N° de suivi (optionnel)" className="bg-transparent border-b py-2 text-sm outline-none w-full sm:w-44" style={{ borderColor: "var(--lo-border)" }} data-testid="ship-tracking-number" />
              <button onClick={ship} disabled={busy} className="btn-primary h-10 px-5 disabled:opacity-50" data-testid="ship-btn">{busy ? "..." : "Marquer expédiée"}</button>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <span className="overline mr-1" style={{ fontSize: "0.5rem", color: "var(--lo-muted)" }}>Notifier le client :</span>
            <button onClick={() => setStatus("prepared", "Commande préparée")} disabled={busy} className="btn-ghost h-8 px-3 text-xs disabled:opacity-50" data-testid="status-prepared">En préparation</button>
            <button onClick={() => setStatus("delivered", "Commande livrée")} disabled={busy} className="btn-ghost h-8 px-3 text-xs disabled:opacity-50" data-testid="status-delivered">Livrée</button>
            <button onClick={() => setStatus("cancelled", "Commande annulée")} disabled={busy} className="btn-ghost h-8 px-3 text-xs disabled:opacity-50" data-testid="status-cancelled">Annulée</button>
            <button onClick={() => setStatus("refunded", "Commande remboursée")} disabled={busy} className="btn-ghost h-8 px-3 text-xs disabled:opacity-50" data-testid="status-refunded">Remboursée</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [orderFilter, setOrderFilter] = useState("all");
  const [reviews, setReviews] = useState([]);
  const [messages, setMessages] = useState([]);

  const loadAll = () => {
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => {});
    api.get("/admin/contact").then((r) => setMessages(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (user === false) nav("/connexion", { state: { from: "/admin" } });
    else if (user && user.role !== "admin") nav("/compte");
    else if (user && user.role === "admin") loadAll();
  }, [user, nav]);

  const toggleReview = async (id, published) => {
    await api.patch(`/admin/reviews/${id}`, { published: !published });
    toast.success(!published ? "Avis publié" : "Avis masqué");
    loadAll();
  };
  const delReview = async (id) => { await api.delete(`/admin/reviews/${id}`); loadAll(); };

  const exportOrdersCsv = async () => {
    try {
      const res = await api.get(`/admin/orders/export?universe=${orderFilter}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `commandes-${orderFilter}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Export CSV téléchargé");
    } catch {
      toast.error("Échec de l'export");
    }
  };

  if (!user || user.role !== "admin") return <div className="pt-40 text-center min-h-[60vh]" style={{ color: "var(--lo-muted)" }}>Chargement...</div>;

  return (
    <div className="pt-32 pb-32 min-h-[70vh] max-w-6xl mx-auto px-6 md:px-12">
      <p className="overline mb-2">Back-office</p>
      <h1 className="font-display text-5xl mb-12" style={{ fontWeight: 300 }}>Administration</h1>

      <Tabs defaultValue="stats">
        <TabsList className="bg-transparent border-b w-full justify-start rounded-none mb-8 h-auto p-0 flex-wrap" style={{ borderColor: "var(--lo-border)" }}>
          {[["stats", "Statistiques"], ["orders", `Commandes (${orders.length})`], ["products", "Produits & coffrets"], ["stock", "Stocks"], ["customers", "Clients"], ["promos", "Codes promo"], ["reviews", `Avis (${reviews.length})`], ["messages", `Messages (${messages.length})`], ["emails", "E-mails"], ["content", "Contenu"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} className="overline data-[state=active]:text-[var(--lo-rose)] data-[state=active]:border-b-2 rounded-none pb-3 px-4 bg-transparent" style={{ borderColor: "var(--lo-rose)" }} data-testid={`tab-${v}`}>{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="stats">
          <AnalyticsPanel />
        </TabsContent>

        <TabsContent value="products">
          <AdminProducts />
        </TabsContent>

        <TabsContent value="stock">
          <AdminStockPanel />
        </TabsContent>

        <TabsContent value="customers">
          <AdminCustomers />
        </TabsContent>

        <TabsContent value="promos">
          <AdminPromos />
        </TabsContent>

        <TabsContent value="content">
          <AdminContent />
        </TabsContent>

        <TabsContent value="orders">
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="order-filters">
            {[["all", "Toutes"], ["femme", "Femme"], ["homme", "Homme"], ["mixte", "Mixte"]].map(([v, l]) => (
              <button key={v} onClick={() => setOrderFilter(v)}
                className="overline px-4 h-9 border transition-colors"
                style={{ borderColor: orderFilter === v ? "var(--lo-rose)" : "var(--lo-border)",
                         color: orderFilter === v ? "var(--lo-rose-hi)" : "var(--lo-muted)",
                         background: orderFilter === v ? "rgba(198,127,137,0.08)" : "transparent" }}
                data-testid={`order-filter-${v}`}>
                {l} ({v === "all" ? orders.length : orders.filter((o) => o.universe === v).length})
              </button>
            ))}
            <button onClick={exportOrdersCsv}
              className="btn-ghost inline-flex items-center gap-2 h-9 px-4 text-xs ml-auto"
              data-testid="orders-export-csv">
              <Download size={14} /> Exporter CSV
            </button>
          </div>
          {(() => {
            const filtered = orderFilter === "all" ? orders : orders.filter((o) => o.universe === orderFilter);
            return filtered.length === 0 ? <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Aucune commande.</p> : (
              <div className="space-y-3">
                {filtered.map((o) => <OrderRow key={o.id} o={o} onShipped={loadAll} />)}
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="emails">
          <AdminEmailLogs />
        </TabsContent>


        <TabsContent value="reviews">
          {reviews.length === 0 ? <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Aucun avis.</p> : (
            <div className="space-y-3">
              {reviews.map((r) => (
                <div key={r.id} className="card-lux p-5 flex justify-between items-start gap-4" data-testid="admin-review">
                  <div className="flex gap-4">
                    {r.photo_url && <img src={`${BACKEND_URL}${r.photo_url}`} alt="" className="w-20 h-20 object-cover shrink-0" style={{ border: "1px solid var(--lo-border)" }} />}
                    <div><p className="font-display text-lg">{r.name} · {r.rating}★ {!r.published && <span className="overline" style={{ color: "var(--lo-muted)" }}>(en attente)</span>}</p>
                      <p className="text-sm mt-1" style={{ color: "var(--lo-muted)" }}>{r.text}</p></div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <button onClick={() => toggleReview(r.id, r.published)} className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: "var(--lo-border-rose)" }} data-testid="review-toggle">
                      {r.published ? <X size={16} /> : <Check size={16} stroke="var(--lo-rose)" />}
                    </button>
                    <button onClick={() => delReview(r.id)} className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: "var(--lo-border)" }} data-testid="review-delete"><Trash2 size={16} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="messages">
          {messages.length === 0 ? <p className="text-sm" style={{ color: "var(--lo-muted)" }}>Aucun message.</p> : (
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className="card-lux p-5" data-testid="admin-message">
                  <p className="font-display text-lg">{m.name} <span className="text-xs" style={{ color: "var(--lo-muted)" }}>· {m.email}</span></p>
                  <p className="text-sm mt-1" style={{ color: "var(--lo-muted)" }}>{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
